import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp } from "lucide-react";
import ExportIcon from "../../icons/ExportIcon";
import InputInlineWidget from "../components/InputInlineWidget";
import MetricCard from "../components/MetricCard";
import { getValueKey } from "../DataUtils";
import { colors as defaultColors } from "../../board/components/TaskDetail/ColorPicker";
import { useNumberData } from "../hooks/useReportData";
import { iconList } from "../ReportSetting";

const ScorecardWidget = ({
  projectId,
  widget,
  onClickUp,
  onClickDown,
  onClickExport,
  onClickSetting,
  onClickDelete,
  onUpdateTitle,
}) => {
  const { t } = useTranslation();

  const objectType = widget.data?.objectType || "SALE";
  const title = widget.data?.title;
  const viewBy = widget.data?.nameKey || "bucketId";
  const segmentBy = widget.data?.segmentBy || "None";
  const measureBy = widget.data?.valueKey || "dealCount";
  const dateUnit = widget.data?.dateUnit || "day";
  const comparisonBy = widget.data?.comparisonBy || "none";
  const filters = widget.data?.filters || {};
  const icons = widget.data?.icons || [];
  const scorecardIcons = icons.length > 0 ? icons : [iconList[0].value];
  const colors = widget.data?.colors || [];
  const scorecardColors = colors.length > 0 ? colors : defaultColors;

  const { data: dataNumber, isLoading: isLoadingNumber } = useNumberData(
    projectId,
    viewBy,
    segmentBy,
    dateUnit,
    getValueKey(measureBy),
    filters,
    objectType,
    true,
    comparisonBy,
    false
  );

  const formatValue = (value: number, measureBy: string) => {
    if (measureBy === "dealValue" || measureBy === "dealValueSum") {
      return `$${value.toLocaleString()}`;
    }
    return value.toLocaleString();
  };

  const getCompareLabel = (cmp: string) => {
    switch (cmp) {
      case "lastYear":
        return t("Last Year");
      case "lastMonth":
        return t("Last Month");
      case "lastWeek":
        return t("Last Week");
      case "lastQuarter":
        return t("Last Quarter");
      default:
        return "";
    }
  };
  const compareText = comparisonBy !== "none" ? `vs. ${getCompareLabel(comparisonBy)}` : "";

  return (
    <MetricCard
      isCenter={true}
      title={
        <InputInlineWidget
          placeholder={t("e.g Deal Performance")}
          value={title}
          className="text-sm sm:text-sm/6 md:text-base font-medium text-gray-600 placeholder:text-gray-400"
          onFinish={onUpdateTitle}
        />
      }
      value={dataNumber ? formatValue(dataNumber.value, measureBy) : "0"}
      delta={dataNumber?.delta || ""}
      deltaTone={dataNumber?.deltaTone || "up"}
      compareText={compareText}
      isLoading={isLoadingNumber}
      icon={iconList.find((item) => item.value === scorecardIcons[0])?.description}
      color={scorecardColors[0]}
      actions={[
        {
          label: t("Up"),
          content: <ArrowUp className="w-4 h-4 text-gray-600" />,
          onClick: (event) => onClickUp(event),
        },
        {
          label: t("Down"),
          content: <ArrowDown className="w-4 h-4 text-gray-600" />,
          onClick: (event) => onClickDown(event),
        },
        {
          label: t("Export"),
          content: <ExportIcon />,
          onClick: () => onClickExport(filters),
        },
      ]}
      onSetting={onClickSetting}
      onDelete={onClickDelete}
    />
  );
};

export default ScorecardWidget;
